/**
 * Who is allowed in, and as what.
 *
 * The rule: an identity that is not on the roster cannot sign in. There is no
 * self-registration — a stranger submits a join request, pays on the link the
 * committee sends, and only the committee's FINAL approval creates the
 * account. Until that moment the applicant is "awaiting approval" and every
 * sign-in attempt is told so. SPEC.md §6.1.
 *
 * ONE credential, TWO identities (owner's direction, 29 Jul 2026). A member
 * signs in with their mobile number OR their email address, plus the same
 * password either way — one field on the form, resolved here. The one-time
 * code path is gone: every SMS cost the club money, and a forgotten password
 * is now recovered by email instead (see reset.js).
 *
 * Role comes from the member record, never from a control on the sign-in
 * screen. A toggle the user can click is a preference, not a permission — so
 * on 29 Jul 2026, at the owner's direction, the Member / Committee toggle was
 * REMOVED from the form entirely. There is one door. You type who you are, and
 * the roster decides which portal opens. A committee account lands on the
 * committee portal; everyone else lands on the member portal.
 */

import { findMemberByEmail, looksLikeEmail, normalizeEmail } from './email';
import { findMemberByPhone, normalizePhone } from './phone';
import { JOIN_IN_PROGRESS } from './join';

/*
 * A portal role is 'member' | 'admin' — which portal someone lands in.
 *
 * A sign-in denial is one of:
 *   NOT_AN_IDENTIFIER | NOT_A_MEMBER | LEFT_CLUB | AWAITING_APPROVAL
 * and a password check adds: NO_PASSWORD | BAD_PASSWORD.
 *
 * NOT_AN_ADMIN is gone with the portal toggle: nobody can ask for the
 * committee portal any more, so nobody can be refused it.
 */

/** A message safe to show on the sign-in screen — never leaks who exists. */
export const DENIAL_MESSAGE = {
  NOT_AN_IDENTIFIER: 'Enter your mobile number or the email address on your membership.',
  NOT_A_MEMBER: 'That is not on the club roster. Request membership below.',
  LEFT_CLUB: 'That membership is no longer active. Please contact the committee.',
  AWAITING_APPROVAL:
    'Your membership is awaiting committee approval. You will be able to sign in as soon as the committee activates it.',
  NO_PASSWORD:
    'No password is set for this account yet. Use “Forgot password” and we’ll email you a link to set one.',
  BAD_PASSWORD: 'That password is not right. Try again, or use “Forgot password”.',
};

/**
 * Which kind of identity was typed, and its stored form.
 *
 * Anything containing an `@` is read as an email attempt — including a
 * malformed one, so `priya@gmail` is told "that email is not valid" rather
 * than "that is not a valid mobile number".
 *
 * Returns { kind: 'email' | 'phone' | null, value }.
 */
export function identify(input) {
  if (looksLikeEmail(input)) {
    return { kind: 'email', value: normalizeEmail(input) };
  }
  return { kind: 'phone', value: normalizePhone(input) };
}

/**
 * The member behind whatever was typed, by either identity. Used by sign-in
 * and by the forgot-password screen, which must accept the same input.
 */
export function findMemberByIdentifier(members, input) {
  const { kind, value } = identify(input);
  if (!value) return null;
  return kind === 'email'
    ? findMemberByEmail(members, value)
    : findMemberByPhone(members, value);
}

/** Whether a join request in the pipeline was filed under this identity. */
function requestMatches(request, kind, value) {
  return kind === 'email'
    ? normalizeEmail(request.email) === value
    : normalizePhone(request.phone) === value;
}

/**
 * Finds the member behind a typed mobile number or email address, and the
 * portal they belong in.
 *
 * There is no "which portal do you want" argument, because there is no such
 * question: `role: 'admin'` on the record opens the committee portal and
 * nothing else does. A coach is a member as far as the portal is concerned —
 * `role` has three values (CLAUDE.md §3.2) but a portal has two.
 *
 * `requests` (optional) lets the screen tell an applicant mid-pipeline the
 * truth — "awaiting approval" — instead of the misleading "not on the
 * roster". Someone who paid yesterday WILL try to sign in today.
 */
export function resolveAccount(members, identifierInput, requests = []) {
  const { kind, value } = identify(identifierInput);
  if (!value) return { account: null, denial: 'NOT_AN_IDENTIFIER' };

  // Live record first — see findMemberByPhone. Matching in array order used to
  // resolve a rejoined member to their old `left` row and refuse them.
  const member =
    kind === 'email' ? findMemberByEmail(members, value) : findMemberByPhone(members, value);

  if (!member) {
    const inFlight = requests.some(
      (r) => requestMatches(r, kind, value) && JOIN_IN_PROGRESS.includes(r.status),
    );
    return { account: null, denial: inFlight ? 'AWAITING_APPROVAL' : 'NOT_A_MEMBER' };
  }

  if (member.status === 'left') return { account: null, denial: 'LEFT_CLUB' };

  // A member the committee added but has not yet ACTIVATED. They hold a bib
  // and may even have paid, but the pipeline ends with the committee's final
  // approval — same as a join request — and until then nobody signs in.
  if (member.status === 'pending') return { account: null, denial: 'AWAITING_APPROVAL' };

  // The roster decides, and only the roster. A committee member is a runner
  // too — they reach their own bib and dues from inside the committee portal.
  return { account: { member, role: isAdmin(member) ? 'admin' : 'member' }, denial: null };
}

/**
 * Password check for the resolved member. Plain comparison in local mode; the
 * real backend never stores a comparable password at all (Firebase Auth).
 * Returns null when the password is good, otherwise a denial key.
 */
export function passwordDenial(member, input) {
  if (!member?.password) return 'NO_PASSWORD';
  if (member.password !== String(input ?? '')) return 'BAD_PASSWORD';
  return null;
}

/** Whether a member record can act on the committee portal. */
export function isAdmin(member) {
  return member?.role === 'admin';
}
